/*
 * Hanlyn welcome popup
 * --------------------
 * Email capture -> optional SMS opt-in -> reveal the promo code.
 *
 * Two things this file is deliberate about:
 *
 * 1. Nothing schedules itself until the visitor has actually done something
 *    (scroll, pointer move, touch, key). Direct-desktop traffic to this store
 *    is roughly half non-human and a load-timer popup would fire at all of it,
 *    poisoning the impression and conversion numbers. Bots do not move a mouse.
 *
 * 2. The email is written to Shopify first and to HighLevel second. Shopify is
 *    the consent record of truth (CASL), so if the HighLevel post fails the
 *    subscriber still exists and still has a timestamped opt-in.
 *
 * Storage keys (mirrored in localStorage and a cookie, per browser):
 *   hanlyn_wp_done      "1" once someone has subscribed. Never shown again.
 *   hanlyn_wp_snooze    ms timestamp of the last dismissal.
 */

(function () {
  'use strict';

  var CONFIG_ID = 'HanlynWelcomePopupConfig';
  var KEY_DONE = 'hanlyn_wp_done';
  var KEY_SNOOZE = 'hanlyn_wp_snooze';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var root = document.querySelector('[data-hanlyn-welcome-popup]');
  if (!root) return;

  var configNode = document.getElementById(CONFIG_ID);
  var config = {};
  try {
    config = JSON.parse(configNode.textContent);
  } catch (e) {
    return;
  }

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------------------------------------------------------------- storage */

  /*
   * Two stores on purpose. localStorage is primary; a mirrored cookie covers
   * the cases where it is unavailable or throws outright - Safari private
   * mode, "block site data" settings, some in-app browsers - and survives a
   * few clears that localStorage does not. Reads prefer whichever store still
   * holds a value, so wiping one alone does not bring the popup back.
   *
   * What this does NOT do: stop the same person on a different browser, a
   * different device, or an incognito window. Nothing client-side can. The
   * real guarantee that the offer cannot be redeemed twice is the discount's
   * own "one use per customer" setting in Shopify.
   */
  var COOKIE_DAYS = 400; /* browsers cap cookie lifetime near this anyway */

  function readCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, days) {
    try {
      var expires = new Date(Date.now() + days * 86400000).toUTCString();
      document.cookie =
        name +
        '=' +
        encodeURIComponent(value) +
        ';expires=' +
        expires +
        ';path=/;SameSite=Lax';
    } catch (e) {
      /* cookies blocked - localStorage may still have taken it */
    }
  }

  function readStore(key) {
    var local = null;
    try {
      local = window.localStorage.getItem(key);
    } catch (e) {
      /* fall through to the cookie */
    }
    return local === null || local === undefined ? readCookie(key) : local;
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode, quota, blocked storage - the cookie below still runs */
    }
    writeCookie(key, value, COOKIE_DAYS);
  }

  function isSuppressed() {
    if (designMode) return false;
    if (readStore(KEY_DONE) === '1') return true;

    var snoozed = parseInt(readStore(KEY_SNOOZE) || '0', 10);
    if (snoozed) {
      var days = config.snoozeDays || 14;
      if (Date.now() - snoozed < days * 86400000) return true;
    }

    if (window.location.search.indexOf('nopopup') > -1) return true;

    var path = window.location.pathname;
    if (path.indexOf('/challenge') === 0 || path.indexOf('/checkouts') === 0) return true;
    if (path.indexOf('/cart') === 0) return true;
    if (path.indexOf('/account') === 0) return true;
    if (path.indexOf('/policies') === 0) return true;

    return false;
  }

  /* ------------------------------------------------------------- open/close */

  var lastFocused = null;
  var isOpen = false;
  var dialog = root.querySelector('[data-wp-dialog]');

  function trapFocus(event) {
    if (event.key !== 'Tab' || !isOpen) return;
    var items = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close('escape');
      return;
    }
    trapFocus(event);
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;

    root.hidden = false;
    /* next frame so the transition has a starting state to animate from */
    window.requestAnimationFrame(function () {
      root.classList.add('is-open');
    });

    document.body.classList.add('hanlyn-wp-locked');
    document.addEventListener('keydown', onKeydown);

    var target = dialog.querySelector('[data-wp-autofocus]:not([hidden])') || dialog;
    window.setTimeout(function () {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }, 220);

    emit('hanlyn:welcome-popup:open');
  }

  function close(reason) {
    if (!isOpen) return;
    isOpen = false;

    root.classList.remove('is-open');
    document.body.classList.remove('hanlyn-wp-locked');
    document.removeEventListener('keydown', onKeydown);

    window.setTimeout(function () {
      root.hidden = true;
    }, 260);

    if (reason !== 'converted') writeStore(KEY_SNOOZE, String(Date.now()));
    if (lastFocused && lastFocused.focus) lastFocused.focus();

    emit('hanlyn:welcome-popup:close', { reason: reason });
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (e) {
      /* older browsers */
    }
  }

  /* -------------------------------------------------------------- triggers */

  var armed = false;
  var timer = null;

  function fire() {
    if (isOpen || isSuppressed()) return;
    disarm();
    open();
  }

  function disarm() {
    window.clearTimeout(timer);
    document.removeEventListener('mouseout', onExitIntent);
    window.removeEventListener('scroll', onScroll);
  }

  function onExitIntent(event) {
    if (window.innerWidth < 750) return;
    if (event.clientY > 6) return;
    if (event.relatedTarget || event.toElement) return;
    fire();
  }

  function onScroll() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    var pct = (window.pageYOffset / scrollable) * 100;
    if (pct >= (config.scrollPercent || 40)) fire();
  }

  function arm() {
    if (armed) return;
    armed = true;

    var delayMs = (config.delaySeconds || 8) * 1000;
    if (isAdVisit()) {
      /* Paid-ad visitors stay about 21 seconds and were spending part of it
         closing the popup. Hold it until they have been here adDelaySeconds
         (counted from page load) or have scrolled scrollPercent of the page. */
      var adMs = (config.adDelaySeconds || 20) * 1000;
      delayMs = Math.max(delayMs, adMs - (Date.now() - LOADED_AT));
    }
    timer = window.setTimeout(fire, Math.max(0, delayMs));

    if (config.exitIntent !== false) {
      document.addEventListener('mouseout', onExitIntent);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Ad visit = a paid click id or paid utm on any page this session, or an
     in-app browser from Facebook or Instagram. Kept in sessionStorage so the
     longer delay carries over when they click through to a product. */
  var LOADED_AT = Date.now();
  var KEY_AD = 'hanlyn_ad_visit';

  function isAdVisit() {
    try {
      if (window.sessionStorage.getItem(KEY_AD) === '1') return true;
    } catch (e) {}
    var ad = false;
    try {
      var q = new URLSearchParams(window.location.search);
      var medium = (q.get('utm_medium') || '').toLowerCase();
      var source = (q.get('utm_source') || '').toLowerCase();
      ad =
        ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'epik'].some(function (k) {
          return q.has(k);
        }) ||
        /^(cpc|ppc|paid|paid[_-]?social|paidsocial|social[_-]?paid|ads?|display|cpm)$/.test(medium) ||
        (/^(facebook|fb|ig|instagram|meta|an|msg)$/.test(source) && medium !== 'email');
    } catch (e) {}
    if (!ad && /FBAN|FBAV|FB_IAB|Instagram/i.test(navigator.userAgent || '')) ad = true;
    if (ad) {
      try {
        window.sessionStorage.setItem(KEY_AD, '1');
      } catch (e) {}
    }
    return ad;
  }

  function waitForHuman() {
    var events = ['scroll', 'mousemove', 'touchstart', 'keydown', 'pointerdown'];

    function onFirst() {
      events.forEach(function (name) {
        window.removeEventListener(name, onFirst);
      });
      arm();
    }

    events.forEach(function (name) {
      window.addEventListener(name, onFirst, { passive: true, once: true });
    });
  }

  /* ---------------------------------------------------------------- steps */

  var steps = {
    email: root.querySelector('[data-wp-step="email"]'),
    sms: root.querySelector('[data-wp-step="sms"]'),
    done: root.querySelector('[data-wp-step="done"]')
  };

  function showStep(name) {
    Object.keys(steps).forEach(function (key) {
      if (steps[key]) steps[key].hidden = key !== name;
    });

    var focusTarget = steps[name] && steps[name].querySelector('[data-wp-autofocus]');
    if (focusTarget) {
      window.setTimeout(function () {
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (e) {
          focusTarget.focus();
        }
      }, 60);
    }
  }

  /* ------------------------------------------------------------ submission */

  var subscriberEmail = '';

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /*
   * Both forms are real Liquid {% form 'customer' %} elements posting into the
   * hidden iframe. That matters: Shopify attaches its bot protection to forms
   * it renders itself, and a hand-built fetch POST to /contact is accepted with
   * a 200 and then quietly dropped without creating a customer. Letting the
   * browser submit for real, into an iframe, is the only way to keep the modal
   * open and still get a customer record with server-stamped consent.
   *
   * Resolves once the iframe has navigated, or after a short grace period so a
   * blocked iframe can never strand someone mid-flow.
   */
  function awaitSink() {
    var sink = document.querySelector('iframe[name="hanlyn-wp-sink"]');

    return new Promise(function (resolve) {
      var settled = false;

      function done() {
        if (settled) return;
        settled = true;
        if (sink) sink.removeEventListener('load', done);
        resolve();
      }

      if (sink) sink.addEventListener('load', done);
      /* never strand anyone if the iframe is blocked or slow */
      window.setTimeout(done, 4000);
    });
  }

  /*
   * HighLevel inbound webhook.
   *
   * This fires on EVERY submission, which is the whole point of it existing
   * alongside the Shopify Flow bridge. Flow can only fire on a Shopify state
   * change - a customer being created, or their consent transitioning - so it
   * structurally cannot see an already-subscribed person submitting again.
   * This can. It is also the only path that carries the phone number and its
   * SMS consent text, which Shopify's newsletter form has no field for.
   *
   * Content-Type MUST be application/json. HighLevel discards a body sent as
   * text/plain and still answers HTTP 200, so that failure is completely
   * invisible - see ghl-inbound-webhook-gotchas.md. That rules out
   * navigator.sendBeacon, which cannot send a JSON content type.
   *
   * The CORS preflight this triggers is answered: the webhook returns
   * access-control-allow-origin: * and access-control-allow-headers: *
   * (verified 2026-09-03). keepalive lets the request outlive the page if the
   * visitor navigates away mid-send.
   *
   * Duplicates are safe: HighLevel upserts by email, so a contact arriving
   * from both here and Flow is matched, not doubled. Do make sure the GHL
   * welcome workflow disallows re-entry, or a repeat submission re-enrols it.
   */
  function postToHighLevel(payload) {
    var url = config.highLevelWebhook;
    if (!url) return;

    try {
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () {
        /* Shopify already holds the consent record; Flow is the backstop */
      });
    } catch (e) {
      /* best effort */
    }
  }

  /*
   * Flat, top-level keys only. HighLevel's inbound-webhook trigger binds
   * top-level payload fields to contact fields; nested objects mostly cannot
   * be mapped at all. Anything you want to branch on in a workflow has to sit
   * at this level. Values are strings so they map into text custom fields
   * without type surprises.
   */
  function buildPayload(extra) {
    var base = {
      source: 'welcome-popup',
      offer_code: config.code || '',
      consent_timestamp: new Date().toISOString(),
      country: (window.Shopify && window.Shopify.country) || '',
      currency:
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '',
      page: window.location.pathname
    };

    Object.keys(extra).forEach(function (key) {
      base[key] = extra[key];
    });

    return base;
  }

  function setBusy(form, busy) {
    var button = form.querySelector('[data-wp-submit]');
    if (!button) return;
    button.disabled = busy;
    button.classList.toggle('is-busy', busy);
  }

  function setError(form, message) {
    var slot = form.querySelector('[data-wp-error]');
    if (!slot) return;
    slot.textContent = message || '';
    slot.hidden = !message;
  }

  /* email step ------------------------------------------------------------ */

  var emailForm = document.getElementById('HanlynWpEmailForm');

  if (emailForm) {
    emailForm.addEventListener('submit', function (event) {
      var input = emailForm.querySelector('[name="contact[email]"]');
      var email = (input.value || '').trim();

      if (!validEmail(email)) {
        event.preventDefault();
        setError(emailForm, config.errorEmail || 'Please enter a valid email address.');
        input.focus();
        return;
      }

      /*
       * Deliberately NOT prevented. The native submit has to run so Shopify's
       * own form handling - including the bot protection it attaches to forms
       * it rendered - executes and the post is accepted. Calling form.submit()
       * from script skips all of that and the customer is never created.
       * The form targets the hidden iframe, so the page does not navigate.
       */
      setError(emailForm, '');
      setBusy(emailForm, true);
      subscriberEmail = email;

      var mirror = root.querySelector('[data-wp-email-mirror]');
      if (mirror) mirror.value = email;

      /*
       * Read BEFORE writeStore below, or this always reports true. Not
       * authoritative - a fresh browser reads false for someone who
       * subscribed last year - so treat it as a hint. The reliable
       * returning-subscriber test is a tag check inside HighLevel.
       */
      var repeatSubmission = readStore(KEY_DONE) === '1';

      writeStore(KEY_DONE, '1');
      postToHighLevel(
        buildPayload({
          email: email,
          phone: '',
          tags: (config.emailTags || ['newsletter']).join(', '),
          step: 'email',
          email_opt_in: 'true',
          sms_opt_in: 'false',
          consent_text: config.emailConsentText || '',
          repeat_submission: repeatSubmission ? 'true' : 'false'
        })
      );
      emit('hanlyn:welcome-popup:subscribe', { email: email, repeat: repeatSubmission });

      awaitSink().then(function () {
        setBusy(emailForm, false);
        showStep(config.collectSms ? 'sms' : 'done');
      });
    });
  }

  /* sms step -------------------------------------------------------------- */

  var smsForm = document.getElementById('HanlynWpSmsForm');

  if (smsForm) {
    smsForm.addEventListener('submit', function (event) {
      var input = smsForm.querySelector('[name="phone"]');
      var consent = smsForm.querySelector('[name="sms_consent"]');
      var phone = (input.value || '').replace(/[^\d+]/g, '');

      if (phone.replace(/\D/g, '').length < 10) {
        event.preventDefault();
        setError(smsForm, config.errorPhone || 'Please enter a valid phone number.');
        input.focus();
        return;
      }

      if (consent && !consent.checked) {
        event.preventDefault();
        setError(smsForm, config.errorConsent || 'Please tick the box so we can text you.');
        consent.focus();
        return;
      }

      if (phone.charAt(0) !== '+') {
        var digits = phone.replace(/\D/g, '');
        phone = digits.length === 10 ? '+1' + digits : '+' + digits;
      }

      /* Same reasoning as the email step - the native submit must run. */
      setError(smsForm, '');
      setBusy(smsForm, true);

      var tags = (config.emailTags || ['newsletter']).concat(config.smsTag || 'sms-optin');

      postToHighLevel(
        buildPayload({
          email: subscriberEmail,
          phone: phone,
          tags: tags.join(', '),
          step: 'sms',
          email_opt_in: 'true',
          sms_opt_in: 'true',
          consent_text: config.smsConsentText || '',
          repeat_submission: 'false'
        })
      );
      emit('hanlyn:welcome-popup:sms', { phone: phone });

      awaitSink().then(function () {
        setBusy(smsForm, false);
        showStep('done');
      });
    });
  }

  var skip = root.querySelector('[data-wp-skip]');
  if (skip) {
    skip.addEventListener('click', function (event) {
      event.preventDefault();
      showStep('done');
    });
  }

  /* copy the code --------------------------------------------------------- */

  var copyButton = root.querySelector('[data-wp-copy]');
  if (copyButton) {
    copyButton.addEventListener('click', function () {
      var code = config.code || '';
      var label = copyButton.querySelector('[data-wp-copy-label]') || copyButton;
      var original = label.textContent;

      function confirmCopy() {
        label.textContent = config.copiedLabel || 'Copied';
        window.setTimeout(function () {
          label.textContent = original;
        }, 2000);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(confirmCopy, function () {});
        return;
      }

      var scratch = document.createElement('textarea');
      scratch.value = code;
      scratch.setAttribute('readonly', '');
      scratch.style.position = 'absolute';
      scratch.style.left = '-9999px';
      document.body.appendChild(scratch);
      scratch.select();
      try {
        document.execCommand('copy');
        confirmCopy();
      } catch (e) {
        /* nothing to do - the code is visible on screen anyway */
      }
      document.body.removeChild(scratch);
    });
  }

  /* ------------------------------------------------------------------ wire */

  root.querySelectorAll('[data-wp-close]').forEach(function (el) {
    el.addEventListener('click', function (event) {
      event.preventDefault();
      close('dismissed');
    });
  });

  var scrim = root.querySelector('[data-wp-scrim]');
  if (scrim) {
    scrim.addEventListener('click', function () {
      close('dismissed');
    });
  }

  /* Anything on the site can open it on purpose, e.g. a footer link. */
  document.addEventListener('click', function (event) {
    var node = event.target;
    if (!node || typeof node.closest !== 'function') return;
    if (!node.closest('[data-hanlyn-welcome-popup-open]')) return;
    event.preventDefault();
    disarm();
    open();
  });

  /* ----------------------------------------------------------------- start */

  showStep('email');

  if (designMode) {
    /* In the theme editor, show it right away so it can be styled. */
    window.setTimeout(open, 400);
    document.addEventListener('shopify:section:select', function (event) {
      if (event.target.contains(root)) open();
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      if (event.target.contains(root)) close('editor');
    });
    return;
  }

  isAdVisit();
  if (isSuppressed()) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForHuman);
  } else {
    waitForHuman();
  }
})();
